import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface MobileSupportOverlayProps {
  isEnabled?: boolean;
}

export const MobileSupportOverlay: React.FC<MobileSupportOverlayProps> = ({ isEnabled = true }): React.ReactElement | null => {
  const storageKey: string = useMemo(() => 'mobile_support_overlay_dismissed_v1', []);
  const [isMobileLike, setIsMobileLike] = useState<boolean>(false);
  const [isDismissed, setIsDismissed] = useState<boolean>(false);

  const detectMobileLike: () => boolean = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;

    const coarsePointer: boolean =
      typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)').matches : false;

    const smallViewport: boolean =
      typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 900px)').matches : window.innerWidth <= 900;

    const touchPoints: number = typeof navigator !== 'undefined' && typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints : 0;

    return (coarsePointer || touchPoints > 0) && smallViewport;
  }, []);

  useEffect(() => {
    if (!isEnabled) return;
    try {
      const dismissedRaw: string | null = localStorage.getItem(storageKey);
      setIsDismissed(dismissedRaw === 'true');
    } catch {
      setIsDismissed(false);
    }

    const update: () => void = (): void => {
      setIsMobileLike(detectMobileLike());
    };

    update();

    const viewportMql: MediaQueryList | null = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 900px)') : null;
    const pointerMql: MediaQueryList | null = typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null;

    const handleMqlChange: () => void = (): void => update();

    if (viewportMql) {
      if (typeof viewportMql.addEventListener === 'function') viewportMql.addEventListener('change', handleMqlChange);
      else if (typeof viewportMql.addListener === 'function') viewportMql.addListener(handleMqlChange);
    }

    if (pointerMql) {
      if (typeof pointerMql.addEventListener === 'function') pointerMql.addEventListener('change', handleMqlChange);
      else if (typeof pointerMql.addListener === 'function') pointerMql.addListener(handleMqlChange);
    }

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      if (viewportMql) {
        if (typeof viewportMql.removeEventListener === 'function') viewportMql.removeEventListener('change', handleMqlChange);
        else if (typeof viewportMql.removeListener === 'function') viewportMql.removeListener(handleMqlChange);
      }

      if (pointerMql) {
        if (typeof pointerMql.removeEventListener === 'function') pointerMql.removeEventListener('change', handleMqlChange);
        else if (typeof pointerMql.removeListener === 'function') pointerMql.removeListener(handleMqlChange);
      }

      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [detectMobileLike, isEnabled, storageKey]);

  const shouldShow: boolean = isEnabled && isMobileLike && !isDismissed;
  if (!shouldShow) return null;

  const handleDismiss: () => void = (): void => {
    setIsDismissed(true);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // ignore
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10050,
        padding: '18px',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(180deg, #0d1117 0%, #0a0e14 100%)',
          border: '2px solid #21262d',
          boxShadow: '0 0 0 1px #161b22, 0 20px 50px rgba(0, 0, 0, 0.8)',
          width: '100%',
          maxWidth: '520px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '2px solid #21262d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '16px',
              color: '#e2e8f0',
              fontWeight: 600,
            }}
            className="font-seurat"
          >
            Mobile support in progress
          </h2>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
            onClick={handleDismiss}
            title="Close"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: '18px',
            fontSize: '14px',
            color: '#c9d1d9',
            lineHeight: '1.6',
          }}
          className="font-seurat"
        >
          <div style={{ marginBottom: '10px', color: '#58a6ff', fontWeight: 700 }}>
            Recommended: play on desktop
          </div>
          <div style={{ marginBottom: '10px' }}>
            This game is still being optimized for phones/tablets.
          </div>
          <div style={{ color: '#9ca3af', fontSize: '13px' }}>
            On mobile you may see input issues, UI overlap, and performance problems.
          </div>
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid #21262d',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
          }}
        >
          <button
            style={{
              backgroundColor: '#238636',
              color: '#ffffff',
              border: '1px solid #2ea043',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
            }}
            onClick={handleDismiss}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};
